import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import axios from 'axios';
import { Menu, X, CheckCircle, Play, Monitor, AlertTriangle, RotateCcw, FileText, Cpu, Layers, Star } from 'react-feather';
import { collection, getDocs, query, where, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from "shared/utils/firebase";
import { getProblemsByLanguage } from './sampleData';
import styles from './CodingPractice.module.css';
import WaveformViewer from './WaveformViewer';
import SchematicViewer from '../CodeBox/SchematicViewer';
import { parseVerilog, propagateLogic } from '../CodeBox/astParser';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE || process.env.REACT_APP_API_BASE || 'http://localhost:8080';
console.log('CodingPractice API_BASE:', API_BASE);

const API_KEY = process.env.REACT_APP_API_KEY || 'siliconlogix-secret-key';
axios.interceptors.request.use((config) => {
  config.headers['X-API-Key'] = API_KEY;
  return config;
}, (error) => {
  return Promise.reject(error);
});

const getProblemCategory = (title) => {
    if (!title) return 'combinational';
    const lower = title.toLowerCase();
    if (lower.includes('adder') || lower.includes('subtractor') || lower.includes('multiplier') || lower.includes('arithmetic')) {
        return 'arithmetic';
    }
    if (lower.includes('flipflop') || lower.includes('latch') || lower.includes('register') || lower.includes('counter') || lower.includes('sequential')) {
        return 'sequential';
    }
    return 'combinational';
};

const getProblemPoints = (diff) => {
    const lower = (diff || '').toLowerCase();
    if (lower === 'easy') return '100 base pts';
    if (lower === 'advanced' || lower === 'hard') return '200 base pts';
    return '150 base pts';
};

const CodingPractice = ({ userDetails, auth: initialAuth, theme, toggleTheme }) => {
    const { language: languageParam } = useParams();
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(initialAuth);

    // Redirect to login if user logs out
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setIsAuthenticated(true);
            } else {
                setIsAuthenticated(false);
                navigate('/login');
            }
        });
        return () => unsubscribe();
    }, [navigate]);


    // Parse language and difficulty from URL parameter (e.g., "verilog-easy")
    const parseLanguageParam = (param) => {
        if (!param) return { language: 'verilog', difficulty: null };

        const parts = param.split('-');
        if (parts.length >= 2) {
            const difficulty = parts[parts.length - 1];
            const language = parts.slice(0, -1).join('-');

            // Check if last part is a valid difficulty
            if (['easy', 'medium', 'advanced'].includes(difficulty.toLowerCase())) {
                return {
                    language: language.toLowerCase(),
                    difficulty: difficulty.charAt(0).toUpperCase() + difficulty.slice(1).toLowerCase()
                };
            }
        }

        // If no difficulty found, treat entire param as language
        return { language: param.toLowerCase(), difficulty: null };
    };

    const { language, difficulty } = parseLanguageParam(languageParam);

    const [problems, setProblems] = useState([]);
    const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
    const [isVerified, setIsVerified] = useState(false);
    const [lastRunFailed, setLastRunFailed] = useState(false);
    const [parsedGraph, setParsedGraph] = useState({ inputs: [], outputs: [], wires: [], gates: [] });
    const [logicValues, setLogicValues] = useState({});
    const [code, setCode] = useState('');
    const [synthesisNetlist, setSynthesisNetlist] = useState(null);
    const [output, setOutput] = useState('');
    const [isRunning, setIsRunning] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [completedProblems, setCompletedProblems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeLeftTab, setActiveLeftTab] = useState('problem');
    const [hintsDrawerOpen, setHintsDrawerOpen] = useState(false);
    const [solutionDrawerOpen, setSolutionDrawerOpen] = useState(false);
    const [showScreenWarning, setShowScreenWarning] = useState(false);
    const [showExitModal, setShowExitModal] = useState(false);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    // Resize states
    const [leftWidth, setLeftWidth] = useState(40); // percentage
    const [editorHeight, setEditorHeight] = useState(70); // percentage of right column
    const [isResizingHorizontal, setIsResizingHorizontal] = useState(false);
    const [isResizingVertical, setIsResizingVertical] = useState(false);

    // Console and Waveform states
    const [activeConsoleTab, setActiveConsoleTab] = useState('output'); // 'output', 'waveform', 'schematic'
    const [vcdContent, setVcdContent] = useState('');
    const [vcdLoading, setVcdLoading] = useState(false);
    const [runDone, setRunDone] = useState(false); // true only after a successful run completes
    const [isConsoleMaximized, setIsConsoleMaximized] = useState(false);

    // Language configuration
    const languageConfigs = {
        verilog: { monaco: 'verilog', extension: '.v' },
        systemverilog: { monaco: 'systemverilog', extension: '.sv' },
        vhdl: { monaco: 'vhdl', extension: '.vhd' },
        matlab: { monaco: 'matlab', extension: '.m' },
        assembly: { monaco: 'asm', extension: '.s' },
        c: { monaco: 'c', extension: '.c' },
        cpp: { monaco: 'cpp', extension: '.cpp' },
        python: { monaco: 'python', extension: '.py' },
        uvm: { monaco: 'systemverilog', extension: '.sv' }
    };

    const currentConfig = languageConfigs[language] || languageConfigs.verilog;

    // Fetch problems from database
    useEffect(() => {
        const fetchProblems = async () => {
            try {
                setLoading(true);

                // Match database category format
                const getCategoryName = (lang) => {
                    const mapping = {
                        'verilog': 'Verilog',
                        'systemverilog': 'SystemVerilog',
                        'vhdl': 'VHDL',
                        'matlab': 'Matlab',
                        'assembly': 'Assembly',
                        'c': 'C',
                        'cpp': 'C++',
                        'python': 'Python',
                        'uvm': 'UVM'
                    };
                    return mapping[lang.toLowerCase()] || (lang.charAt(0).toUpperCase() + lang.slice(1));
                };
                const categoryName = getCategoryName(language);

                // Build query for codeExercises collection
                let q;
                if (difficulty) {
                    // Query with both category and difficulty
                    q = query(
                        collection(db, 'codeExercises'),
                        where('category', '==', categoryName),
                        where('difficulty', '==', difficulty),
                        where('isPublished', '==', true)
                    );
                } else {
                    // Query with only category (all difficulties)
                    q = query(
                        collection(db, 'codeExercises'),
                        where('category', '==', categoryName),
                        where('isPublished', '==', true)
                    );
                }

                const snapshot = await getDocs(q);
                const fetchedTests = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        title: data.title || 'Untitled Problem',
                        description: data.description || 'No description available',
                        difficulty: data.difficulty || 'Medium',
                        order: data.order || 1,
                        initialCode: data.initialCode || '',
                        hints: data.hints || [],
                        solution: data.solution || '',
                        testCases: data.testCases || '',
                        testbenchCode: data.testbenchCode || data.tbCode || data.tbTemplate || ''
                    };
                });

                // Sort by order field to maintain sequence
                fetchedTests.sort((a, b) => a.order - b.order);

                if (fetchedTests.length === 0) {
                    // Use sample problems from sampleData.js as fallback
                    const sampleProblems = getProblemsByLanguage(language);
                    setProblems(sampleProblems); // Show all sample problems
                } else {
                    setProblems(fetchedTests);
                }

                // Set initial code for first problem
                if (fetchedTests.length > 0) {
                    setCode(fetchedTests[0].initialCode || '');
                } else {
                    const sampleProblems = getProblemsByLanguage(language);
                    if (sampleProblems.length > 0) {
                        setCode(sampleProblems[0].initialCode || '');
                    }
                }
            } catch (error) {
                console.error('Error fetching problems:', error);
                // Fallback to sample data on error
                const sampleProblems = getProblemsByLanguage(language);
                setProblems(sampleProblems);
                if (sampleProblems.length > 0) {
                    setCode(sampleProblems[0].initialCode || '');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchProblems();
    }, [language, difficulty]);

    // Screen size detection
    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            setScreenWidth(width);

            // Show warning if screen is too small
            if (width < 768) {
                setShowScreenWarning(true);
            }
        };

        // Check on mount
        handleResize();

        // Add event listener
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch user progress
    useEffect(() => {
        const fetchUserProgress = async () => {
            if (!auth.currentUser || problems.length === 0) return;

            try {
                const userProgressRef = collection(db, 'codeProgress');
                const q = query(
                    userProgressRef,
                    where('userId', '==', auth.currentUser.uid),
                    where('language', '==', language), // optimize query
                    where('difficulty', '==', difficulty) // optimize query
                );

                // Note: The above optimized query assumes we store language/difficulty. 
                // If not, we might need to fetch all for user or use specific IDs.
                // Let's stick to a simpler approach: fetch all for user for now, or fetch by ID if possible.
                // Actually, let's construct IDs as `${userId}_${problemId}`.

                // Better approach: Check status for each problem in the list
                // OR fetch all 'codeProgress' for this user and map.
                // Since we want to load fast, let's fetch all completion records for this user.
                const qAll = query(
                    collection(db, 'codeProgress'),
                    where('userId', '==', auth.currentUser.uid)
                );

                const snapshot = await getDocs(qAll);
                const completedIds = new Set();
                snapshot.forEach(doc => {
                    const data = doc.data();
                    if (data.problemId) completedIds.add(data.problemId);
                });

                const completedIndices = [];
                problems.forEach((problem, index) => {
                    if (completedIds.has(problem.id)) {
                        completedIndices.push(index);
                    }
                });

                setCompletedProblems(completedIndices);

            } catch (error) {
                console.error('Error fetching user progress:', error);
            }
        };

        fetchUserProgress();
    }, [problems, language, difficulty]);

    useEffect(() => {
        // Reset code when problem changes
        if (problems[currentProblemIndex]) {
            setCode(problems[currentProblemIndex].initialCode || '');
            setOutput('');
            setActiveConsoleTab('output');
            setVcdContent('');
            setSynthesisNetlist(null);
            setIsVerified(false);
            setLastRunFailed(false);
            setRunDone(false);
            setParsedGraph({ inputs: [], outputs: [], wires: [], gates: [] });
            setActiveLeftTab('problem');
            setHintsDrawerOpen(false);
            setSolutionDrawerOpen(false);
        }

        // Scroll to top
        const contentArea = document.querySelector(`.${styles.problemContent}`);
        if (contentArea) {
            contentArea.scrollTop = 0;
        }
    }, [currentProblemIndex, problems]);



    const handleToggleInput = (port) => {
        setLogicValues(prev => {
            const next = { ...prev, [port]: prev[port] ? 0 : 1 };
            return propagateLogic(parsedGraph, next);
        });
    };

    const handleProblemClick = (index) => {
        setCurrentProblemIndex(index);
        setIsSidebarOpen(false);
    };



    const handleMarkComplete = async () => {
        if (!isVerified) {
            alert("⚠️ Submission Blocked! Your code must compile, run, and pass all test bench assertions first. Click 'Run' to verify your solution.");
            return;
        }
        if (!completedProblems.includes(currentProblemIndex)) {
            setCompletedProblems([...completedProblems, currentProblemIndex]);

            // Save to Firestore
            if (auth.currentUser && problems[currentProblemIndex]) {
                try {
                    const problem = problems[currentProblemIndex];
                    const docId = `${auth.currentUser.uid}_${problem.id}`;
                    await setDoc(doc(db, 'codeProgress', docId), {
                        userId: auth.currentUser.uid,
                        problemId: problem.id,
                        language: language,
                        difficulty: difficulty, // Store this for easier aggregation later
                        completedAt: serverTimestamp(),
                        title: problem.title
                    });
                } catch (error) {
                    console.error("Error saving progress:", error);
                }
            }
        }
        // Auto-advance to next problem
        if (currentProblemIndex < problems.length - 1) {
            setTimeout(() => {
                setCurrentProblemIndex(currentProblemIndex + 1);
            }, 500);
        }
    };

    const handleRun = async () => {
        setIsRunning(true);
        setRunDone(false);
        setParsedGraph({ inputs: [], outputs: [], wires: [], gates: [] });
        setSynthesisNetlist(null);
        setVcdContent('');
        setOutput('Compiling and running...');
        setActiveConsoleTab('output');
        try {
            const currentProblem = problems[currentProblemIndex] || {};
            const ext = currentConfig.extension || '.v';
            
            // Build the files array. Send user code as design.v/sv and built-in testbench if present
            const files = [
                {
                    name: `design${ext}`,
                    content: code
                },
                currentProblem.testbenchCode && {
                    name: `tb${ext}`,
                    content: currentProblem.testbenchCode
                }
            ].filter(Boolean);

            // Auto-detect top module name from testbench code
            let reqTopModule = 'tb';
            if (currentProblem.testbenchCode) {
                const match = currentProblem.testbenchCode.match(/module\s+(\w+)/);
                if (match && match[1]) {
                    reqTopModule = match[1];
                }
            }

            const payload = {
                language: language,
                files,
                top_module: reqTopModule,
                wave_dump: 'vcd',
                timeout_sec: 20
            };

            // Start synthesis concurrently on the backend for Verilog/SystemVerilog
            let synthPromise = null;
            if (language === 'verilog' || language === 'systemverilog') {
                const synthPayload = {
                    language,
                    files,
                    run_mode: 'synthesis',
                    top_module: currentProblem.topModule || reqTopModule || 'fulladd',
                    timeout_sec: 20
                };
                synthPromise = axios.post(`${API_BASE}/runs`, synthPayload)
                    .then(async (res) => {
                        const sId = res.data.run_id || res.data.id;
                        if (!sId) return null;
                        const finalS = await pollRun(sId);
                        if (finalS.status === 'completed') {
                            const { data } = await axios.get(`${API_BASE}/runs/${sId}/artifacts`);
                            const list = data.artifacts || data || [];
                            const netlistFile = list.find(a => 
                                (a.filename || a.name || '').toLowerCase() === 'netlist.json'
                            );
                            if (netlistFile) {
                                const fpath = netlistFile.filename || netlistFile.name;
                                const netRes = await axios.get(`${API_BASE}/artifacts/${sId}/${fpath}`);
                                return typeof netRes.data === 'string' ? netRes.data : JSON.stringify(netRes.data);
                            }
                        }
                        return null;
                    })
                    .catch(err => {
                        console.error('Background synthesis error:', err);
                        return null;
                    });
            }

            const res = await axios.post(`${API_BASE}/runs`, payload);
            const runId = res.data.run_id || res.data.id;

            if (!runId) throw new Error('No run ID returned');

            setOutput(`Run submitted: ${runId}\nWaiting for completion...`);

            // Poll for results in parallel
            const [finalRun, stdout, stderr, synthResult] = await Promise.all([
                pollRun(runId),
                fetchStdout(runId),
                fetchStderr(runId),
                synthPromise || Promise.resolve(null)
            ]);

            if (synthResult) {
                setSynthesisNetlist(synthResult);
            }

            setOutput(`Status: ${finalRun.status}\n\n----- OUTPUT -----\n${stdout}\n\n----- ERRORS -----\n${stderr}`);

            // Validation Assertions Checking
            const isSuccessExit = finalRun.status === 'completed' && finalRun.exit_code === 0;
            const hasCompError = stderr.toLowerCase().includes('error') || stderr.toLowerCase().includes('fail');
            
            const stdUpper = stdout.toUpperCase();
            const errUpper = stderr.toUpperCase();
            
            const hasAssertionFailure = stdUpper.includes('FAIL') || 
                                         stdUpper.includes('ERROR') || 
                                         stdUpper.includes('MISMATCH') ||
                                         errUpper.includes('FAIL') ||
                                         errUpper.includes('ERROR');
            
            const passed = isSuccessExit && !hasCompError && !hasAssertionFailure;
            setIsVerified(passed);
            setLastRunFailed(!passed);

            // Parse schematic from synthesis netlist or code after run completes
            if (language === 'verilog' || language === 'systemverilog') {
                try {
                    const graph = synthResult
                        ? parseVerilog(synthResult)
                        : parseVerilog(code);
                    setParsedGraph(graph);
                    setLogicValues(prev => {
                        const next = {};
                        graph.inputs.forEach(inp => {
                            next[inp] = prev[inp] !== undefined ? prev[inp] : 0;
                        });
                        return propagateLogic(graph, next);
                    });
                } catch (err) {
                    console.error('Post-run schematic parse error:', err);
                }
            }

            // Mark all tabs ready
            setRunDone(true);
            setActiveConsoleTab('result'); // Auto-switch to result tab!

            // Fetch VCD if it's a Verilog/SystemVerilog/UVM and run was successful
            if ((language === 'verilog' || language === 'systemverilog' || language === 'uvm') && finalRun.status === 'completed') {
                fetchVcdArtifact(runId);
            }

        } catch (err) {
            console.error(err);
            const errorMsg = err.message || 'Failed to run code';
            setOutput(`Error connecting to ${API_BASE}: ${errorMsg}\n\nNote: Make sure the backend server is running and your .env files are correctly configured.`);
        } finally {
            setIsRunning(false);
        }
    };

    const fetchVcdArtifact = async (runId) => {
        try {
            setVcdLoading(true);
            const { data } = await axios.get(`${API_BASE}/runs/${runId}/artifacts`);
            const artifacts = data.artifacts || data || [];

            // Find VCD file - check both 'filename' (backend default) and 'name' (fallback)
            const vcdFile = artifacts.find(a =>
                (a.filename && a.filename.toLowerCase().endsWith('.vcd')) ||
                (a.name && a.name.toLowerCase().endsWith('.vcd'))
            );

            if (vcdFile) {
                const fpath = vcdFile.filename || vcdFile.name;
                const vcdRes = await axios.get(`${API_BASE}/artifacts/${runId}/${fpath}`);
                setVcdContent(typeof vcdRes.data === 'string' ? vcdRes.data : JSON.stringify(vcdRes.data));
            } else {
                setVcdContent('');
            }
        } catch (err) {
            console.error('Error fetching VCD:', err);
            setVcdContent('');
        } finally {
            setVcdLoading(false);
        }
    };

    async function pollRun(runId) {
        const maxMs = 30000;
        const start = Date.now();
        let delay = 150;
        while (true) {
            const { data } = await axios.get(`${API_BASE}/runs/${runId}`);
            if (data.status === 'completed' || data.status === 'failed') return data;
            if (Date.now() - start > maxMs) return data;
            await new Promise(r => setTimeout(r, delay));
            delay = Math.min(delay * 1.5, 1000);
        }
    }

    async function fetchStdout(runId) {
        try {
            const { data } = await axios.get(`${API_BASE}/runs/${runId}/stdout`);
            return data.stdout ?? '';
        } catch {
            return '';
        }
    }

    async function fetchStderr(runId) {
        try {
            const { data } = await axios.get(`${API_BASE}/runs/${runId}/stderr`);
            return data.stderr ?? '';
        } catch {
            return '';
        }
    }

    // Horizontal resize handler (left/right columns)
    const handleHorizontalMouseDown = (e) => {
        e.preventDefault();
        setIsResizingHorizontal(true);
    };

    const handleHorizontalMouseMove = (e) => {
        if (!isResizingHorizontal) return;

        const container = document.querySelector(`.${styles.contentArea}`);
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const newLeftWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

        // Constrain between 20% and 70%
        if (newLeftWidth >= 20 && newLeftWidth <= 70) {
            setLeftWidth(newLeftWidth);
        }
    };

    const handleHorizontalMouseUp = () => {
        setIsResizingHorizontal(false);
    };

    // Vertical resize handler (editor/output)
    const handleVerticalMouseDown = (e) => {
        e.preventDefault();
        setIsResizingVertical(true);
    };

    const handleVerticalMouseMove = (e) => {
        if (!isResizingVertical) return;

        const container = document.querySelector(`.${styles.rightColumn}`);
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const newEditorHeight = ((e.clientY - containerRect.top) / containerRect.height) * 100;

        // Constrain between 30% and 85%
        if (newEditorHeight >= 30 && newEditorHeight <= 85) {
            setEditorHeight(newEditorHeight);
        }
    };

    const handleVerticalMouseUp = () => {
        setIsResizingVertical(false);
    };

    // Add event listeners for resize
    useEffect(() => {
        if (isResizingHorizontal) {
            document.addEventListener('mousemove', handleHorizontalMouseMove);
            document.addEventListener('mouseup', handleHorizontalMouseUp);
        } else {
            document.removeEventListener('mousemove', handleHorizontalMouseMove);
            document.removeEventListener('mouseup', handleHorizontalMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleHorizontalMouseMove);
            document.removeEventListener('mouseup', handleHorizontalMouseUp);
        };
    }, [isResizingHorizontal, leftWidth]);

    useEffect(() => {
        if (isResizingVertical) {
            document.addEventListener('mousemove', handleVerticalMouseMove);
            document.addEventListener('mouseup', handleVerticalMouseUp);
        } else {
            document.removeEventListener('mousemove', handleVerticalMouseMove);
            document.removeEventListener('mouseup', handleVerticalMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleVerticalMouseMove);
            document.removeEventListener('mouseup', handleVerticalMouseUp);
        };
    }, [isResizingVertical, editorHeight]);

    if (loading || problems.length === 0) {
        return (
            <div className={styles.loading}>
                <div className={styles.spinner}></div>
                <p>{loading ? 'Loading problems...' : 'No problems available'}</p>
            </div>
        );
    }

    const currentProblem = problems[currentProblemIndex];

    return (
        <div className={`${styles.practiceContainer} ${theme === 'light' ? styles.lightTheme : ''}`}>
            {/* Screen Size Warning Modal */}
            {showScreenWarning && (
                <div className={styles.screenWarningOverlay}>
                    <div className={styles.screenWarningModal}>
                        <div className={styles.warningIcon}>
                            <Monitor size={48} />
                        </div>
                        <h2>Screen Size Notice</h2>
                        <p className={styles.warningMessage}>
                            For the best coding practice experience, we recommend using a device with a larger screen (minimum 768px width).
                        </p>
                        <div className={styles.warningDetails}>
                            <div className={styles.warningItem}>
                                <AlertTriangle size={18} />
                                <span>Current screen: {screenWidth}px</span>
                            </div>
                            <div className={styles.warningItem}>
                                <Monitor size={18} />
                                <span>Recommended: 768px or larger</span>
                            </div>
                        </div>
                        <p className={styles.warningSubtext}>
                            The code editor, problem statement, and output console require adequate space for comfortable viewing and editing.
                        </p>
                        <div className={styles.warningActions}>
                            <button
                                className={styles.warningBtnSecondary}
                                onClick={() => navigate('/exercise')}
                            >
                                Go Back
                            </button>
                            <button
                                className={styles.warningBtnPrimary}
                                onClick={() => setShowScreenWarning(false)}
                            >
                                Continue Anyway
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showExitModal && (
                <div className={styles.screenWarningOverlay}>
                    <div className={styles.screenWarningModal}>
                        <div className={styles.warningIcon} style={{ color: '#cf2860', backgroundColor: '#ffebee' }}>
                            <AlertTriangle size={48} />
                        </div>
                        <h2>Exit Exercise?</h2>
                        <p className={styles.warningMessage}>
                            Are you sure you want to cancel the current exercise and return to the list page? Any unsaved changes in the editor will be lost.
                        </p>
                        <div className={styles.warningActions}>
                            <button
                                className={styles.warningBtnSecondary}
                                onClick={() => setShowExitModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.warningBtnPrimary}
                                style={{ backgroundColor: '#cf2860' }}
                                onClick={() => navigate('/exercise')}
                            >
                                Confirm Exit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile Overlay */}
            {isSidebarOpen && (
                <div className={styles.overlay} onClick={() => setIsSidebarOpen(false)} />
            )}

            {/* Main Content */}
            <div className={styles.mainContent}>
                {/* Content Area */}
                <div className={styles.contentArea}>
                    {/* Sidebar - Problems List */}
                    <div className={`${styles.sidebar} ${isSidebarOpen ? styles.sidebarOpen : ''}`}>
                        <div className={styles.sidebarHeader}>
                            <h3>Problems</h3>
                            <button
                                className={styles.closeBtn}
                                onClick={() => setIsSidebarOpen(false)}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className={styles.problemsList}>
                            {problems.map((problem, index) => (
                                <div
                                    key={problem.id}
                                    className={`${styles.problemItem} ${currentProblemIndex === index ? styles.activeProblem : ''
                                        } ${completedProblems.includes(index) ? styles.completedProblem : ''}`}
                                    onClick={() => handleProblemClick(index)}
                                >
                                    <span className={styles.problemNumber}>{index + 1}</span>
                                    <div className={styles.problemInfo}>
                                        <span className={styles.problemItemTitle}>{problem.title}</span>
                                        <span className={`${styles.difficulty} ${styles[problem.difficulty.toLowerCase()]}`}>
                                            {problem.difficulty}
                                        </span>
                                    </div>
                                    {completedProblems.includes(index) && (
                                        <CheckCircle size={16} className={styles.checkIcon} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* Left Column - Problem Statement & Testbench Tabs */}
                    <div className={styles.leftColumn} style={{ flex: `0 0 ${leftWidth}%` }}>
                        <div className={styles.problemSection}>
                            <div className={styles.problemHeader}>
                                <div className={styles.problemHeaderTop}>
                                    <button
                                        className={styles.menuToggle}
                                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                                    >
                                        <Menu size={20} />
                                    </button>
                                    <div className={styles.headerTitle}>
                                        <h3>{language.charAt(0).toUpperCase() + language.slice(1)} Exercise</h3>
                                    </div>
                                    <button className={styles.linkButton} onClick={() => setShowExitModal(true)}><X size={14} /> Close</button>
                                </div>
                                <div className={styles.leftHeaderTabs}>
                                    <button
                                        className={`${styles.leftTabBtn} ${activeLeftTab === 'problem' ? styles.activeLeftTab : ''}`}
                                        onClick={() => setActiveLeftTab('problem')}
                                    >
                                        Problem
                                    </button>
                                    <button
                                        className={`${styles.leftTabBtn} ${activeLeftTab === 'testbench' ? styles.activeLeftTab : ''}`}
                                        onClick={() => setActiveLeftTab('testbench')}
                                    >
                                        Testbench
                                    </button>
                                </div>
                            </div>
                            
                            {activeLeftTab === 'problem' ? (
                                <div className={styles.problemContent}>
                                    <div className={styles.prominentTitleContainer}>
                                        <h2 className={styles.mainQuestionTitle}>
                                            {currentProblemIndex + 1}. {currentProblem.title}
                                        </h2>
                                        <div className={styles.badgesRow}>
                                            <span className={`${styles.badgeDifficulty} ${styles['badge' + currentProblem.difficulty]}`}>
                                                {currentProblem.difficulty}
                                            </span>
                                            <span className={styles.badgeCategory}>
                                                <Layers size={12} className={styles.badgeIcon} />
                                                {getProblemCategory(currentProblem.title)}
                                            </span>
                                            <span className={styles.badgePoints}>
                                                <Star size={12} className={styles.badgeIcon} />
                                                {getProblemPoints(currentProblem.difficulty)}
                                            </span>
                                        </div>
                                    </div>
                                    <hr className={styles.dividerLine} />

                                    <div 
                                        className={styles.description} 
                                        dangerouslySetInnerHTML={{ 
                                            __html: (currentProblem.description || "")
                                                .replace(/&lt;/g, "<")
                                                .replace(/&gt;/g, ">")
                                                .replace(/&quot;/g, '"')
                                                .replace(/&#39;/g, "'")
                                                .replace(/&amp;/g, "&")
                                        }} 
                                    />

                                    {/* Collapsible hints drawer */}
                                    {currentProblem.hints && currentProblem.hints.length > 0 && (
                                        <div className={styles.drawerSection}>
                                            <button 
                                                className={styles.drawerHeader}
                                                onClick={() => setHintsDrawerOpen(!hintsDrawerOpen)}
                                            >
                                                <span className={styles.drawerTitle}>💡 Hints</span>
                                                <span className={styles.drawerIcon}>{hintsDrawerOpen ? '▼' : '▶'}</span>
                                            </button>
                                            {hintsDrawerOpen && (
                                                <div className={styles.drawerBody}>
                                                    <ul className={styles.drawerList}>
                                                        {currentProblem.hints.map((hint, idx) => (
                                                            <li key={idx} className={styles.drawerListItem}>{hint}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Collapsible solution drawer */}
                                    {currentProblem.solution && (
                                        <div className={styles.drawerSection}>
                                            <button 
                                                className={styles.drawerHeader}
                                                onClick={() => setSolutionDrawerOpen(!solutionDrawerOpen)}
                                            >
                                                <span className={styles.drawerTitle}>✅ Solution</span>
                                                <span className={styles.drawerIcon}>{solutionDrawerOpen ? '▼' : '▶'}</span>
                                            </button>
                                            {solutionDrawerOpen && (
                                                <div className={styles.drawerBody}>
                                                    <div className={styles.solutionCodeContainer}>
                                                        <button 
                                                            className={styles.copyBtn}
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(currentProblem.solution);
                                                                alert('Solution copied to clipboard!');
                                                            }}
                                                        >
                                                            Copy
                                                        </button>
                                                        <pre className={styles.solutionCode}>
                                                            <code>{currentProblem.solution}</code>
                                                        </pre>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {currentProblem.testCases && (
                                        <div className={styles.testCasesBox}>
                                            <h4>Test Cases:</h4>
                                            <p>{currentProblem.testCases}</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className={styles.testbenchContent}>
                                    <div className={styles.testbenchHeader}>
                                        <span className={styles.testbenchReadOnlyLabel}>
                                            Read-only · copy into your IDE to test locally
                                        </span>
                                        <button 
                                            className={styles.copyBtn}
                                            onClick={() => {
                                                navigator.clipboard.writeText(currentProblem.testbenchCode || '');
                                                alert('Testbench code copied to clipboard!');
                                            }}
                                            style={{
                                                padding: '0.25rem 0.6rem',
                                                fontSize: '0.75rem',
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #ced4da',
                                                borderRadius: '4px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Copy
                                        </button>
                                    </div>
                                    <div className={styles.testbenchEditorWrapper}>
                                        <Editor
                                            height="100%"
                                            language={currentConfig.monaco}
                                            value={currentProblem.testbenchCode || `// No built-in testbench code provided for this exercise.`}
                                            theme={theme === 'light' ? 'vs' : 'vs-dark'}
                                            options={{
                                                readOnly: true,
                                                minimap: { enabled: false },
                                                fontSize: 13,
                                                lineNumbers: 'on',
                                                scrollBeyondLastLine: false,
                                                automaticLayout: true,
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Horizontal Resize Handle */}
                    <div
                        className={styles.resizeHandleHorizontal}
                        onMouseDown={handleHorizontalMouseDown}
                    />

                    {/* Right Column - Code Editor & Output */}
                    <div className={styles.rightColumn}>
                        {/* Code Editor */}
                        <div className={styles.editorSection} style={isConsoleMaximized ? { flex: '0 0 auto', borderBottom: 'none' } : { flex: `0 0 ${editorHeight}%` }}>
                            <div className={styles.editorHeader}>
                                <span>Code Editor</span>
                                <div className={styles.editorActions}>
                                    <button
                                        className={styles.resetBtn}
                                        onClick={() => {
                                            if (window.confirm("Are you sure you want to reset your code to the initial template? This will overwrite your current progress in the editor.")) {
                                                setCode(currentProblem.initialCode || '');
                                            }
                                        }}
                                        title="Reset to Initial Code"
                                    >
                                        <RotateCcw size={16} />
                                        Reset
                                    </button>
                                    <button
                                        className={styles.runBtn}
                                        onClick={handleRun}
                                        disabled={isRunning}
                                    >
                                        <Play size={16} />
                                        {isRunning ? 'Running...' : 'Run'}
                                    </button>
                                    <button
                                        className={styles.completeBtn}
                                        onClick={handleMarkComplete}
                                    >
                                        <CheckCircle size={16} />
                                        {completedProblems.includes(currentProblemIndex) ? 'Submitted' : 'Submit Code'}
                                    </button>
                                </div>
                            </div>
                            {!isConsoleMaximized && (
                                <div className={styles.editorWrapper}>
                                    <Editor
                                        height="100%"
                                        language={currentConfig.monaco}
                                        value={code}
                                        onChange={(value) => setCode(value || '')}
                                        theme={theme === 'light' ? 'vs' : 'vs-dark'}
                                        options={{
                                            minimap: { enabled: false },
                                            fontSize: 14,
                                            lineNumbers: 'on',
                                            scrollBeyondLastLine: false,
                                            automaticLayout: true,
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Vertical Resize Handle */}
                        {!isConsoleMaximized && (
                            <div
                                className={styles.resizeHandleVertical}
                                onMouseDown={handleVerticalMouseDown}
                            />
                        )}

                        {/* Output Console with Tabs */}
                        <div className={styles.outputSection} style={isConsoleMaximized ? { flex: 1, height: '100%' } : {}}>
                            <div className={styles.outputHeaderTabs} style={{ justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', gap: '0.15rem', alignItems: 'center' }}>
                                    <button
                                        className={`${styles.consoleTabBtn} ${activeConsoleTab === 'result' ? styles.activeConsoleTab : ''}`}
                                        onClick={() => setActiveConsoleTab('result')}
                                    >
                                        <CheckCircle size={14} />
                                        Result
                                    </button>
                                    <button
                                        className={`${styles.consoleTabBtn} ${activeConsoleTab === 'output' ? styles.activeConsoleTab : ''}`}
                                        onClick={() => setActiveConsoleTab('output')}
                                    >
                                        <FileText size={14} />
                                        Output
                                    </button>
                                    {(language === 'verilog' || language === 'systemverilog' || language === 'uvm') && (
                                        <>
                                            <button
                                                className={`${styles.consoleTabBtn} ${activeConsoleTab === 'waveform' ? styles.activeConsoleTab : ''}`}
                                                onClick={() => setActiveConsoleTab('waveform')}
                                            >
                                                <Monitor size={14} />
                                                Waveform
                                            </button>
                                            <button
                                                className={`${styles.consoleTabBtn} ${activeConsoleTab === 'schematic' ? styles.activeConsoleTab : ''}`}
                                                onClick={() => setActiveConsoleTab('schematic')}
                                            >
                                                <Cpu size={14} />
                                                Schematic
                                            </button>
                                        </>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', paddingRight: '4px' }}>
                                    <button 
                                        onClick={() => setIsConsoleMaximized(prev => !prev)}
                                        style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', transition: 'color 0.2s', padding: '2px 6px' }}
                                        title={isConsoleMaximized ? "Restore Editor View" : "Maximize Console View"}
                                    >
                                        {isConsoleMaximized ? '🗗' : '🗖'}
                                    </button>
                                </div>
                            </div>
                            <div className={styles.outputContent}>
                                {activeConsoleTab === 'result' && (
                                    isRunning ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '8px', color: '#64748b' }}>
                                            <div className={styles.smallSpinner} />
                                            <span style={{ fontSize: '11px' }}>Running testbench verification...</span>
                                        </div>
                                    ) : runDone ? (
                                        <div className={styles.resultContainer}>
                                            <div className={styles.resultHeader}>
                                                <div className={styles.resultHeaderTitle}>
                                                    <h3>Simulation Result</h3>
                                                    {isVerified ? (
                                                        <span className={styles.statusBadgePassed}>Passed</span>
                                                    ) : (
                                                        <span className={styles.statusBadgeFailed}>Failed</span>
                                                    )}
                                                </div>
                                                <div className={styles.gradeScoreContainer}>
                                                    <span className={styles.gradeScoreLabel}>Evaluation Score</span>
                                                    <span className={styles.gradeScore}>{isVerified ? '100/100' : '0/100'}</span>
                                                </div>
                                            </div>
                                            <div className={styles.evalDetailsBox}>
                                                <h4>Verification Checklist</h4>
                                                <ul className={styles.evalDetailsList}>
                                                    <li className={styles.evalDetailItem}>
                                                        {runDone && !lastRunFailed ? (
                                                            <CheckCircle size={16} className={styles.evalIconCheck} />
                                                        ) : (
                                                            <AlertTriangle size={16} className={styles.evalIconCross} />
                                                        )}
                                                        <span>Code Compilation: {runDone && !lastRunFailed ? 'Success' : 'Passed with warnings / errors'}</span>
                                                    </li>
                                                    <li className={styles.evalDetailItem}>
                                                        {isVerified ? (
                                                            <CheckCircle size={16} className={styles.evalIconCheck} />
                                                        ) : (
                                                            <AlertTriangle size={16} className={styles.evalIconCross} />
                                                        )}
                                                        <span>Testbench Assertions: {isVerified ? 'All passed successfully' : 'Assertions failed or simulation issues found'}</span>
                                                    </li>
                                                    <li className={styles.evalDetailItem}>
                                                        {vcdContent ? (
                                                            <CheckCircle size={16} className={styles.evalIconCheck} />
                                                        ) : (
                                                            <AlertTriangle size={16} className={styles.evalIconCross} />
                                                        )}
                                                        <span>Waveform Signal Generation: {vcdContent ? 'VCD generated' : 'No VCD data found'}</span>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '8px', color: '#64748b', textAlign: 'center', padding: '20px' }}>
                                            <span style={{ fontSize: '24px' }}>🏆</span>
                                            <strong style={{ fontSize: '12px', color: 'var(--text-main)' }}>No Result Yet</strong>
                                            <p style={{ fontSize: '11px', margin: 0, maxWidth: '260px', lineHeight: '1.5' }}>Run your code to run the testbench verification and see your grade.</p>
                                        </div>
                                    )
                                )}
                                {activeConsoleTab === 'output' && (
                                    isRunning ? (
                                        <pre style={{ color: 'var(--text-muted)' }}>{output}</pre>
                                    ) : runDone ? (
                                        <pre>{output}</pre>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '8px', color: '#64748b', textAlign: 'center', padding: '20px' }}>
                                            <span style={{ fontSize: '24px' }}>▶</span>
                                            <strong style={{ fontSize: '12px', color: 'var(--text-main)' }}>No Output Yet</strong>
                                            <p style={{ fontSize: '11px', margin: 0, maxWidth: '260px', lineHeight: '1.5' }}>Click <strong>Run</strong> to compile your Verilog code and see the simulation output here.</p>
                                        </div>
                                    )
                                )}
                                {activeConsoleTab === 'waveform' && (
                                    <div className={styles.consoleWaveformContainer}>
                                        {isRunning ? (
                                            <div className={styles.vcdLoading}>
                                                <div className={styles.smallSpinner} />
                                                <span>Running simulation...</span>
                                            </div>
                                        ) : vcdLoading ? (
                                            <div className={styles.vcdLoading}>
                                                <div className={styles.smallSpinner} />
                                                <span>Loading Waveform...</span>
                                            </div>
                                        ) : runDone ? (
                                            <WaveformViewer vcdContent={vcdContent} />
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '8px', color: '#64748b', textAlign: 'center', padding: '20px' }}>
                                                <span style={{ fontSize: '24px' }}>📈</span>
                                                <strong style={{ fontSize: '12px', color: 'var(--text-main)' }}>No Waveform Data</strong>
                                                <p style={{ fontSize: '11px', margin: 0, maxWidth: '260px', lineHeight: '1.5' }}>Run your code to generate signal waveforms from the simulation testbench.</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {activeConsoleTab === 'schematic' && (
                                    <div className={styles.consoleSchematicContainer}>
                                        {isRunning ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '8px', color: '#64748b' }}>
                                                <div className={styles.smallSpinner} />
                                                <span style={{ fontSize: '11px' }}>Synthesizing schematic...</span>
                                            </div>
                                        ) : runDone ? (
                                            <SchematicViewer
                                                theme={theme}
                                                problemTitle={currentProblem.title}
                                                parsedGraph={parsedGraph}
                                                logicValues={logicValues}
                                                onToggleInput={handleToggleInput}
                                            />
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '8px', color: '#64748b', textAlign: 'center', padding: '20px' }}>
                                                <span style={{ fontSize: '24px' }}>🔌</span>
                                                <strong style={{ fontSize: '12px', color: 'var(--text-main)' }}>Schematic Not Available</strong>
                                                <p style={{ fontSize: '11px', margin: 0, maxWidth: '260px', lineHeight: '1.5' }}>Run your code to generate the gate-level logic schematic from your Verilog design.</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default CodingPractice;
